// i18n setup for all supported app languages. Choice persists in localStorage; first visit is
// auto-filled from the user's location, then browser language, then English.
//
// Location is inferred from the device IANA timezone (Intl API), so language
// selection does not need an IP geolocation call.
//
// The pure resolution logic lives in ./resolve (side-effect-free, unit-tested).

import { createI18n } from 'vue-i18n';
import en from './en.json';
import et from './et.json';
import ru from './ru.json';
import lv from './lv.json';
import zh from './zh.json';
import th from './th.json';
import { resolveLocale, SUPPORTED_LOCALES, type LocaleCode } from './resolve';

export { SUPPORTED_LOCALES };
export type { LocaleCode };

const LS_KEY = 'autospy.lang';

function detectLocale(): LocaleCode {
  let timeZone: string | undefined;
  try {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    timeZone = undefined;
  }
  const saved = typeof localStorage !== 'undefined' ? localStorage.getItem(LS_KEY) : null;
  const languages =
    typeof navigator !== 'undefined' ? (navigator.languages ?? [navigator.language]) : [];
  return resolveLocale({ saved, timeZone, languages });
}

export const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale: detectLocale(),
  fallbackLocale: 'en',
  messages: { en, et, ru, lv, zh, th },
});

if (typeof document !== 'undefined') document.documentElement.lang = i18n.global.locale.value;

export function currentLocale(): LocaleCode {
  return i18n.global.locale.value as LocaleCode;
}

export function setLocale(code: LocaleCode): void {
  i18n.global.locale.value = code;
  document.documentElement.lang = code;
  localStorage.setItem(LS_KEY, code);
}
